import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { AskScottBubble } from "./AskScottBubble";
import { AskScottPanel } from "./AskScottPanel";

const HIDE_PATTERNS: RegExp[] = [
  /^\/blog\/(?!index$|tags$|tag\/)[^/]+$/, // individual blog post
  /^\/admin(\/.*)?$/,
  /^\/login$/,
];

const NUDGE_DISMISSED_KEY = "ask-scott-nudge-dismissed";
const NUDGE_DELAY_MS = 20_000;

export function AskScottWidget() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const panelWrapRef = useRef<HTMLDivElement>(null);

  const hidden = HIDE_PATTERNS.some((re) => re.test(pathname));

  // Nudge timer
  useEffect(() => {
    if (hidden) return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(NUDGE_DISMISSED_KEY) === "1") return;
    const t = window.setTimeout(() => {
      if (!open) setShowNudge(true);
    }, NUDGE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [hidden, open]);

  // Close nudge when panel opens
  useEffect(() => {
    if (open) setShowNudge(false);
  }, [open]);

  // Click outside to minimize
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      const node = panelWrapRef.current;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  if (hidden) return null;

  const dismissNudge = () => {
    setShowNudge(false);
    try {
      localStorage.setItem(NUDGE_DISMISSED_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2 md:bottom-6 md:right-6">
      {open && (
        <div ref={panelWrapRef}>
          <AskScottPanel onClose={() => setOpen(false)} />
        </div>
      )}
      {!open && showNudge && (
        <div className="ask-scott-nudge relative max-w-[260px] rounded-2xl border-2 border-foreground bg-background px-4 py-3 pr-8 text-sm shadow-pop">
          <p className="leading-snug">Ask me about my experience or content approach.</p>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={dismissNudge}
            className="absolute right-1.5 top-1.5 grid h-6 w-6 place-items-center rounded-full text-foreground/60 hover:bg-foreground/10 hover:text-foreground"
          >
            <svg
              viewBox="0 0 16 16"
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 3l10 10M13 3L3 13" strokeLinecap="round" />
            </svg>
          </button>
          <div className="absolute -bottom-1.5 right-6 h-3 w-3 rotate-45 border-b-2 border-r-2 border-foreground bg-background" />
        </div>
      )}
      {!open && <AskScottBubble onClick={() => setOpen(true)} />}
    </div>
  );
}
